/**
 * ============================================================================
 * tableService.ts - ENTERPRISE API VERSION
 * ============================================================================
 * 
 * This service handles all table operations through secure server APIs.
 * Direct Firestore writes are NO LONGER ALLOWED under enterprise security rules.
 * 
 * READ operations still use Firestore directly (onSnapshot, getDoc).
 * WRITE operations go through authenticated Cloud Function endpoints.
 * 
 *  FIX: Added client-side validation for seat change during active hand

 * ============================================================================
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  DocumentData,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { API_BASE } from '../utils/apiBase';
// GameState is defined below for export compatibility

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiCall<T>(endpoint: string, body: object): Promise<T> {
  const token = await getAuthToken();
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `API call failed: ${response.status}`);
  }
  
  return data;
}

// ============================================================================
// TYPES
// ============================================================================

export interface GameTableData {
  id: string;
  name: string;
  ownerId: string;
  createdBy: string;
  players: string[];
  playerBuyIns: { [uid: string]: number };
  readyPlayers: string[];
  status: 'waiting' | 'ready' | 'playing' | 'paused' | 'ended';
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  maxSeats: number;
  currency: 'playcoins' | 'pkcash';
  gameState?: GameState;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  //  Special Rules

  specialRules?: {
    sevenTwoGame?: boolean;
    sevenTwoBounty?: number;
    bombPotEnabled?: boolean;
    bombPotFrequency?: number;
    bombPotAnte?: number;
    straddleEnabled?: boolean;
    runItTwiceEnabled?: boolean;
    allInOrFold?: boolean;
  };
  //  Pending Bomb Pot

  pendingBombPot?: boolean;
  pendingBombPotAnte?: number;
  pendingBombPotTriggeredBy?: string;
  // Voice/Video
  voiceChatEnabled?: boolean;
  voiceChatParticipants?: number;
  // Private table
  isPrivate?: boolean;
  privateCode?: string;
}


export interface GameState {
  players: any[];
  dealer: string;
  smallBlindSeat: number;
  bigBlindSeat: number;
  currentSeat: number;
  pot: number;
  communityCards: any[];
  round: string;
  bets: { [key: string]: number };
  timestamp?: number;
  status?: string;
  currentPlayerIndex?: number;
  handNumber?: number;
  //  Special Rules State

  specialRules?: {
    sevenTwoGame?: boolean;
    sevenTwoBounty?: number;
    bombPotEnabled?: boolean;
    straddleEnabled?: boolean;
    runItTwiceEnabled?: boolean;
    allInOrFold?: boolean;
  };
  //  Bomb Pot

  isBombPot?: boolean;
  bombPotAnte?: number;
  //  7-2 Bounty

  sevenTwoBountyPaid?: boolean;
  sevenTwoBountyResult?: {
    winnerId: string;
    winnerName: string;
    bountyPerPlayer: number;
    totalBounty: number;
    payers: string[];
    handNumber: number;
  };
  //  Security

  showdownComplete?: boolean;
  actionsLocked?: boolean;
}

// ============================================================================
// READ OPERATIONS (Direct Firestore - still allowed)
// ============================================================================

/**
 * Get a single table by ID
 */
export const getTable = async (tableId: string): Promise<GameTableData | null> => {
  const tableRef = doc(db, 'tables', tableId);
  const tableDoc = await getDoc(tableRef);
  
  if (!tableDoc.exists()) return null;
  
  return { id: tableDoc.id, ...tableDoc.data() } as GameTableData;
};

/**
 * Subscribe to table updates
 */
export const subscribeToTable = (
  tableId: string,
  callback: (table: GameTableData | null) => void
): (() => void) => {
  const tableRef = doc(db, 'tables', tableId);
  
  return onSnapshot(tableRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    callback({ id: snapshot.id, ...snapshot.data() } as GameTableData);
  });
};

/**
 * Get all public tables
 */
export const getPublicTables = async (): Promise<GameTableData[]> => {
  const tablesRef = collection(db, 'tables');
  const q = query(
    tablesRef,
    where('isPrivate', '==', false),
    where('status', 'in', ['waiting', 'ready', 'playing']),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GameTableData));
};

/**
 * Get tables created by a user
 */
export const getUserTables = async (userId: string): Promise<GameTableData[]> => {
  const tablesRef = collection(db, 'tables');
  const q = query(
    tablesRef,
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(20)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GameTableData));
};

// ============================================================================
// WRITE OPERATIONS (Through API)
// ============================================================================

/**
 * Create a new table
 *  ENTERPRISE: Uses server API

 */
export const createTable = async (params: {
  name: string;
  smallBlind: number;
  bigBlind: number;
  minBuyIn: number;
  maxBuyIn: number;
  maxSeats?: number;
  currency?: 'playcoins' | 'pkcash';
  isPrivate?: boolean;
}): Promise<{ tableId: string }> => {
  console.log('🎮 [TableService] Creating table via API');
  return apiCall('/createTable', { stakes: params.bigBlind, ...params });
};

/**
 * Join a table
 *  ENTERPRISE: Uses secure join endpoint

 */
export const joinTable = async (tableId: string, buyInAmount?: number): Promise<void> => {
  console.log(`🎮 [TableService] Joining table ${tableId} via API`);
  await apiCall('/secureJoinTable', { tableId, buyInAmount });
};

/**
 * Leave a table
 *  ENTERPRISE: Uses secure leave endpoint

 */
export const leaveTable = async (tableId: string): Promise<void> => {
  console.log(`👋 [TableService] Leaving table ${tableId} via API`);
  await apiCall('/secureLeaveTable', { tableId });
};

/**
 * Set player buy-in amount
 *  ENTERPRISE: Uses server API

 */
export const setPlayerBuyIn = async (
  tableId: string,
  amount: number
): Promise<void> => {
  console.log(`💰 [TableService] Setting buy-in to ${amount}`);
  await apiCall('/setPlayerBuyIn', { tableId, amount });
};

/**
 * Update player ready status
 *  ENTERPRISE: Uses server API

 */
export const updatePlayerReady = async (
  tableId: string,
  userId: string,
  isReady: boolean
): Promise<void> => {
  console.log(`✋ [TableService] Setting ready=${isReady} for player`);
  await apiCall('/updatePlayerReady', { tableId, isReady });
};

/**
 * Clear all ready players
 *  ENTERPRISE: Uses server API (owner only)

 */
export const clearReadyPlayers = async (tableId: string): Promise<void> => {
  console.log(`🔄 [TableService] Clearing ready players`);
  await apiCall('/clearReadyPlayers', { tableId });
};

/**
 * Set player waiting status
 *  ENTERPRISE: Uses server API

 */
export const setPlayerWaiting = async (
  tableId: string,
  userId: string,
  isWaiting: boolean
): Promise<void> => {
  console.log(`⏸️ [TableService] Setting waiting=${isWaiting}`);
  await apiCall('/setPlayerWaiting', { tableId, isWaiting });
};

/**
 * Change seat
 *  ENTERPRISE: Uses server API

 *  FIX #10: Added client-side validation to block seat change during active hand

 */
export const changeSeat = async (
  tableId: string,
  userId: string,
  newSeat: number,
  currentGameState?: GameState
): Promise<{ success: boolean; error?: string }> => {
  try {
    //  FIX #10: Client-side validation - block during active hand

    if (currentGameState) {
      const isHandInProgress = currentGameState.status === 'playing' && 
                               currentGameState.round !== 'ended';
      
      if (isHandInProgress) {
        console.warn(`🚫 [TableService] Cannot change seat during active hand`);
        return { 
          success: false, 
          error: 'Cannot change seats during an active hand. Wait for the hand to complete.' 
        };
      }
    }
    
    console.log(`🪑 [TableService] Changing to seat ${newSeat}`);
    const result = await apiCall<{ success: boolean }>('/changeSeat', { tableId, newSeat });
    return result;
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Update table status
 *  ENTERPRISE: Uses server API (owner only)

 */
export const updateTableStatus = async (
  tableId: string,
  status: 'waiting' | 'ready' | 'playing' | 'paused' | 'ended'
): Promise<void> => {
  console.log(`📊 [TableService] Updating status to ${status}`);
  await apiCall('/updateTableStatus', { tableId, status });
};

/**
 * Reset table to lobby
 *  ENTERPRISE: Uses server API (owner only)

 */
export const resetTableToLobby = async (tableId: string): Promise<void> => {
  console.log(`🔄 [TableService] Resetting table to lobby`);
  await apiCall('/resetTableToLobby', { tableId });
};

/**
 * Delete table
 *  ENTERPRISE: Uses server API (owner only)

 */
export const deleteTable = async (tableId: string): Promise<boolean> => {
  try {
    console.log(`🗑️ [TableService] Deleting table`);
    await apiCall('/deleteTable', { tableId });
    return true;
  } catch (error) {
    console.error('Failed to delete table:', error);
    return false;
  }
};

/**
 * Update game state
 *  ENTERPRISE: Uses server API (owner only)

 */
export const updateGameState = async (
  tableId: string,
  gameState: GameState
): Promise<void> => {
  console.log(`📝 [TableService] Updating game state via API`);
  await apiCall('/updateGameState', { tableId, gameState });
};

/**
 * Save game state (alias for updateGameState)
 */
export const saveGameState = updateGameState;

/**
 * Save countdown state
 *  ENTERPRISE: Uses server API (owner only)

 */
export const saveCountdownState = async (
  tableId: string,
  countdownState: any
): Promise<void> => {
  console.log(`⏱️ [TableService] Saving countdown state`);
  await apiCall('/saveCountdownState', { tableId, countdownState });
};

/**
 * Reload chips (rebuy)
 *  ENTERPRISE: Uses secure rebuy endpoint

 */
export const reloadChips = async (
  tableId: string,
  amount: number,
  currency: 'playcoins' | 'pkcash' = 'playcoins'
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`💰 [TableService] Reloading ${amount} chips (${currency})`);
    const result = await apiCall<{ success: boolean; newChips?: number; error?: string }>(
      '/secureRebuy', 
      { tableId, amount, currency }
    );
    return result;
  } catch (error) {
    console.error('Rebuy failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Rebuy failed' 
    };
  }
};

/**
 * Join game at specific seat
 *  ENTERPRISE: Uses server API

 */
export const joinGameAtSeat = async (
  tableId: string,
  userId: string,
  username: string,
  seatIndex: number,
  buyInAmount: number,
  markAsWaiting: boolean = false //  If true, player joins but waits for next hand

): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`🪑 [TableService] Joining at seat ${seatIndex} with ${buyInAmount} chips${markAsWaiting ? ' (waiting for next hand)' : ''}`);
    const result = await apiCall<{ success: boolean }>('/joinGameAtSeat', { 
      tableId, 
      seatIndex,
      buyInAmount,
      markAsWaiting,
    });
    return result;
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Remove player from game
 *  ENTERPRISE: Uses secure leave endpoint

 */
export const removePlayerFromGame = async (
  tableId: string,
  userId: string
): Promise<{ success: boolean; chipsReturned: number }> => {
  console.log(`👋 [TableService] Removing player from game`);
  return apiCall('/secureLeaveTable', { tableId });
};

// ============================================================================
// LEGACY COMPATIBILITY FUNCTIONS
// These wrap the API calls to maintain backward compatibility
// ============================================================================

/**
 * Update pot (now handled by game engine on server)
 * @deprecated Use playerAction for betting
 */
export const updatePot = async (tableId: string, amount: number): Promise<void> => {
  console.warn('updatePot is deprecated - pot is managed by server game engine');
  // Pot updates happen automatically through playerAction
};

/**
 * Update table players list
 * @deprecated Use joinTable/leaveTable instead
 */
export const updateTablePlayers = async (tableId: string, playerIds: string[]): Promise<void> => {
  console.warn('updateTablePlayers is deprecated - use join/leave APIs');
};

/**
 * Save hand result
 * @deprecated Hand results are saved by server during showdown
 */
export const saveHandResult = async (
  tableId: string,
  result: any
): Promise<void> => {
  console.warn('saveHandResult is deprecated - handled by server showdown resolution');
};

// ============================================================================
// BACKWARD COMPATIBILITY ALIASES (must be before export object)
// ============================================================================

/**
 * Alias for subscribeToTable (old name)
 */
export const listenToTable = subscribeToTable;

/**
 * Alias for getPublicTables (old name)
 */
export const getAllTables = getPublicTables;

/**
 * Listen to tables by currency
 */
export const listenToTablesByCurrency = (
  currency: 'playcoins' | 'pkcash',
  callback: (tables: GameTableData[]) => void
): (() => void) => {
  const tablesRef = collection(db, 'tables');
  const q = query(
    tablesRef,
    where('currency', '==', currency),
    where('status', 'in', ['waiting', 'ready', 'playing']),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  
  return onSnapshot(q, (snapshot) => {
    const tables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GameTableData));
    callback(tables);
  });
};

/**
 * Get hand history for a table
 */
export const getHandHistory = async (
  tableId: string,
  limitNum: number = 20
): Promise<any[]> => {
  const historyRef = collection(db, 'tables', tableId, 'handHistory');
  const q = query(historyRef, orderBy('timestamp', 'desc'), limit(limitNum));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/**
 * Cleanup all listeners (no-op for API-based service)
 */
export const cleanupAllListeners = (): void => {
  console.log('cleanupAllListeners: API-based service, no listeners to clean');
};

/**
 * Get player wallet
 */
export const getPlayerWallet = async (userId: string): Promise<{
  userId: string;
  playCoins: number;
  pkCash: number;
} | null> => {
  const walletRef = doc(db, 'wallets', userId);
  const walletDoc = await getDoc(walletRef);
  
  if (!walletDoc.exists()) return null;
  
  return { userId, ...walletDoc.data() } as any;
};

/**
 * Get player stats
 */
export const getPlayerStats = async (userId: string): Promise<{
  userId: string;
  handsPlayed: number;
  handsWon: number;
  totalWinnings: number;
} | null> => {
  const statsRef = doc(db, 'playerStats', userId);
  const statsDoc = await getDoc(statsRef);
  
  if (!statsDoc.exists()) return null;
  
  return { userId, ...statsDoc.data() } as any;
};

/**
 * Get leaderboard
 */
export const getLeaderboard = async (limitNum: number = 10): Promise<any[]> => {
  const statsRef = collection(db, 'playerStats');
  const q = query(statsRef, orderBy('totalWinnings', 'desc'), limit(limitNum));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({ odD: doc.id, ...doc.data() }));
};

/**
 * Get player's recent hands
 */
export const getPlayerRecentHands = async (
  odD: string,
  limitNum: number = 10
): Promise<any[]> => {
  console.warn('getPlayerRecentHands: Use getHandHistory(tableId) for specific table history');
  return [];
};

/**
 * @deprecated Wallet updates now happen server-side
 */
export const updateWalletAfterHand = async (
  odD: string,
  currency: 'playcoins' | 'pkcash',
  amount: number,
  tableId: string
): Promise<void> => {
  console.warn('updateWalletAfterHand is DEPRECATED - wallet updates happen server-side');
};

/**
 * @deprecated Use /secureRebuy endpoint instead
 */
export const addChipsToWallet = async (
  odD: string,
  currency: 'playcoins' | 'pkcash',
  amount: number
): Promise<void> => {
  console.warn('addChipsToWallet is DEPRECATED - use gameApiService.secureRebuy()');
};

/**
 * @deprecated Stats updates happen server-side during showdown resolution
 */
export const updatePlayerStats = async (
  odD: string,
  stats: { handsPlayed?: number; handsWon?: number; totalWinnings?: number }
): Promise<void> => {
  console.warn('updatePlayerStats is DEPRECATED - stats updates happen server-side');
};

/**
 * @deprecated Batch wallet updates happen server-side
 */
export const batchUpdatePlayerWallets = async (
  updates: Array<{ odD: string; currency: string; amount: number }>
): Promise<void> => {
  console.warn('batchUpdatePlayerWallets is DEPRECATED - wallet updates happen server-side');
};

// ============================================================================
// SERVICE OBJECT EXPORT
// ============================================================================

export const tableService = {
  // Read operations
  getTable,
  subscribeToTable,
  listenToTable,
  getPublicTables,
  getAllTables,
  getUserTables,
  listenToTablesByCurrency,
  getHandHistory,
  
  // Write operations (API-based)
  createTable,
  joinTable,
  leaveTable,
  setPlayerBuyIn,
  updatePlayerReady,
  clearReadyPlayers,
  setPlayerWaiting,
  changeSeat,
  updateTableStatus,
  resetTableToLobby,
  deleteTable,
  updateGameState,
  saveGameState,
  saveCountdownState,
  reloadChips,
  joinGameAtSeat,
  removePlayerFromGame,
  
  // Deprecated/Legacy
  updatePot,
  updateTablePlayers,
  saveHandResult,
  cleanupAllListeners,
  
  // Wallet & Stats
  getPlayerWallet,
  getPlayerStats,
  getLeaderboard,
  getPlayerRecentHands,
  updateWalletAfterHand,
  addChipsToWallet,
  updatePlayerStats,
  batchUpdatePlayerWallets,
};

export default tableService;